import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { orderBy, noop } from 'lodash-es';
import Breadcrumb from 'sushiweb/Breadcrumb/Breadcrumb';
import H5 from 'sushiweb/Typography/Heading/H5';
import Button from 'sushiweb/Button/Button';
import grey from 'sushiweb/tokens/color/grey';
import { reportsHeadColumn } from '../common/components/DataTables/components/headColumns';
import {
  BreadcrumbHeaderWrapper,
  BreadCrumbWrapper,
  DashboardWrapper,
  TableContainer,
  TabWrapper,
} from '../common/styles';
import ErrorPage from '../common/components/ErrorPage';
import {
  applicableFlowTypes,
  currentFlowTypeEnum,
  REFRESH_LABEL,
  REPORTS,
} from '../common/components/constants';
import DataTables from '../common/components/DataTables';
import { DASHBOARD_ROUTE, HOME_ROUTE } from '../../constants/routes';
import { TableHeaderWrapper } from '../Payments/styles';
import CreateReportModal from './CreateReportModal';
import { formatDateSorting } from '../../helpers';
import { ReportsSVG } from '../../components/SVGs/commonSVGs';
import EmptyStateV2 from '../common/components/EmptyStateV2/EmptyStateV2';
import { comingSoonReportsTab, emptyReportsStateTitle } from '../../constants';
import ZTrackingClient from '../../jumbo/ZTrackingClient';
import Banner from '../common/components/Banner';

const Reports = ({
  isReportsTabEnabled,
  currentFlow,
  getAllReports,
  fetchingReports,
  fetchedReports,
  selectedWorkspace,
  reports,
  getAllGroups,
  clearGenerateReport,
  viewOnlyMode,
  getCompanyInvoices,
  getAllCompanyMappedOffices,
}) => {
  if (currentFlow === currentFlowTypeEnum.FLOW_TYPE_PPI) {
    return (
      <DashboardWrapper>
        <ErrorPage />
      </DashboardWrapper>
    );
  }
  const { reportsData = [] } = reports;
  const [isReportsEmpty, setIsReportsEmpty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentData, setCurrentData] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [typingTimeout, setTypingTimeout] = useState(0);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    ZTrackingClient.recordImpression({
      ui_source: 'REPORTS',
      component: 'SIDE_NAV_BAR',
    });
  }, []);

  useEffect(() => {
    if (selectedWorkspace && selectedWorkspace.workspace_id) {
      getAllReports({ workspaceId: selectedWorkspace.workspace_id });
      getAllGroups({
        workspaceId: selectedWorkspace.workspace_id,
        flowType: currentFlow,
      });
      getAllCompanyMappedOffices();
      getCompanyInvoices({ flowType: currentFlow });
    }
  }, [selectedWorkspace]);

  useEffect(() => {
    if (!fetchingReports) {
      if (reportsData) {
        if (reportsData.length === 0) {
          setIsReportsEmpty(true);
        } else {
          setIsReportsEmpty(false);
          reportsData.sort(
            (a, b) =>
              formatDateSorting(b.created_on) - formatDateSorting(a.created_on),
          );
        }
        setSearchResults(reportsData);
        setCurrentData(reportsData.slice(0, 10));
      }
      setLoading(false);
    }
  }, [fetchingReports]);

  useEffect(() => {
    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }
    const timeout = setTimeout(() => {
      if (!fetchingReports) {
        setSearchResults(reportsData);
        handlePageChange(1);
      }
    }, 500);

    return () => setTypingTimeout(timeout);
  }, [fetchingReports]);

  useEffect(() => {
    if (searchResults) {
      const start = currentPage * 10 - 10;
      const end = currentPage * 10;
      const rangeData = searchResults.slice(start, end);
      setCurrentData(rangeData);
    }
  }, [currentPage, searchResults]);

  const handlePageChange = page => {
    setCurrentPage(page);
  };

  const refreshPage = () => {
    getAllReports(selectedWorkspace.workspace_id);
  };

  const handleSort = (column, sortDirection) => {
    setSearchResults(orderBy(searchResults, column.selector, sortDirection));
  };

  const closeModal = () => {
    setShowModal(false);
    getAllReports({ workspaceId: selectedWorkspace.workspace_id });
    clearGenerateReport();
  };

  const ActionComponent = () => {
    return (
      <>
        <CreateReportModal
          title='Generate report'
          showModal={showModal}
          closeModal={closeModal}
        />
        <Button
          onClick={() => {
            setShowModal(true);
          }}
          icon={ReportsSVG()}
          iconAlign='right'
          style={{ marginTop: `${isReportsEmpty ? '0px' : '-15px'}` }}
          disabled={viewOnlyMode}
        >
          Generate report
        </Button>
      </>
    );
  };

  if (!isReportsTabEnabled) {
    return (
      <DashboardWrapper>
        <ErrorPage errorMsg={comingSoonReportsTab} />
      </DashboardWrapper>
    );
  }

  if (isReportsEmpty) {
    return (
      <>
        <EmptyStateV2
          state={REPORTS}
          title={emptyReportsStateTitle}
          actionComponent={ActionComponent}
        />
      </>
    );
  }

  return (
    <DashboardWrapper>
      {viewOnlyMode && <Banner />}
      <BreadcrumbHeaderWrapper>
        <BreadCrumbWrapper>
          <Breadcrumb>
            <Breadcrumb.Link
              style={{ color: 'var(--coolgreycoolgrey-400, #AFB4C0)' }}
              linkTo={`${DASHBOARD_ROUTE}#${HOME_ROUTE}`}
            >
              Home
            </Breadcrumb.Link>
            <Breadcrumb.Link
              style={{ color: 'var(--coolgreycoolgrey-800, #414A5D)' }}
            >
              Reports
            </Breadcrumb.Link>
          </Breadcrumb>
        </BreadCrumbWrapper>
      </BreadcrumbHeaderWrapper>
      <TableHeaderWrapper>
        <H5>Past Reports</H5>
        <ActionComponent />
      </TableHeaderWrapper>
      <TabWrapper>
        <TableContainer
          style={{ borderRadius: '20px', border: `0.1rem solid ${grey.z100}` }}
        >
          {!fetchedReports ? (
            <ErrorPage
              showAction
              actionLabel={REFRESH_LABEL}
              clickAction={refreshPage}
            />
          ) : (
            <DataTables
              columns={reportsHeadColumn}
              loading={fetchingReports || loading}
              data={currentData}
              pagination
              totalRows={searchResults.length}
              handlePageChange={handlePageChange}
              onSort={handleSort}
              emptyStateTitle='No reports'
            />
          )}
        </TableContainer>
      </TabWrapper>
    </DashboardWrapper>
  );
};

Reports.propTypes = {
  isReportsTabEnabled: PropTypes.bool,
  currentFlow: PropTypes.oneOf(applicableFlowTypes),
  selectedWorkspace: PropTypes.objectOf(PropTypes.any),
  getAllReports: PropTypes.func,
  fetchingReports: PropTypes.bool,
  fetchedReports: PropTypes.bool,
  reports: PropTypes.objectOf(PropTypes.any),
  getAllGroups: PropTypes.func,
  getAllCompanyMappedOffices: PropTypes.func,
  clearGenerateReport: PropTypes.func,
  getCompanyInvoices: PropTypes.func,
  viewOnlyMode: PropTypes.bool,
};

Reports.defaultProps = {
  isReportsTabEnabled: true,
  currentFlow: currentFlowTypeEnum.FLOW_TYPE_PPI,
  selectedWorkspace: {},
  getAllReports: noop,
  fetchingReports: true,
  fetchedReports: true,
  reports: {},
  getAllGroups: noop,
  clearGenerateReport: noop,
  getAllCompanyMappedOffices: noop,
  getCompanyInvoices: noop,
  viewOnlyMode: false,
};

export default Reports;
